import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit ,Inject} from '@angular/core';
import { ApiCallService } from '../../Services/ApiCallService';   // same path as in timelessmonk.ts
import { Title, Meta } from '@angular/platform-browser';

export interface TeamProfile {
  ProfileID: number;
  Slug: string;
  Name: string;
  Designation: string | null;
  Headline: string | null;
  Description: string | null;
  MoreHeadline: string | null;
  MoreDescription: string | null;
  Image: string | null;
}

@Component({
  selector: 'app-mandavi-s-mishra',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './mandavi-s-mishra.html',
  styleUrl: './mandavi-s-mishra.css',
  host: { ngSkipHydration: 'true' },
})
export class MandaviSMishra implements OnInit {
  imageBaseUrl = 'https://crm-api.redmonkwellness.com';
  private readonly SLUG = 'mandavvi-s-mishra';   // must match the Slug saved in the dashboard



  profile: TeamProfile | null = null;

  constructor(
    private api: ApiCallService, private cdr: ChangeDetectorRef,
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit(): void {

    this.setSeo();

    this.api.postData('Team', 'GetTeamData', {}).subscribe({
      next: (res: any) => {
        if (!(res?.success ?? res?.Success)) return;

        this.profile = (res.Profiles || []).find((p: TeamProfile) => p.Slug === this.SLUG) || null;
        this.cdr.detectChanges();
      },
      error: (err) => console.error(err),
    });
  }

  // blank line = new paragraph
  paragraphs(text: string | null | undefined): string[] {
    return (text || '').split(/\r?\n\s*\r?\n/).map(p => p.trim()).filter(p => p);
  }

  // /assets/... stays as is, uploaded images get the API url
  contentImg(path: string | null | undefined): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/assets')) return path;
    return this.imageBaseUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
  }




private setSeo(): void {
  const pageTitle =
    'Mandavvi S. Mishra | Founder & Director | Red Monk Wellness';

  const description =
    'Meet Mandavvi S. Mishra, Founder & Director of Red Monk Wellness, and learn about her journey, vision and role in building the wellness brand.';

  const canonicalUrl =
    'https://www.redmonkwellness.com/mandavi-s-mishra';

  const ogImage =
    'https://www.redmonkwellness.com/assets/images/manadavi-s-mishra.jpeg';

  // TITLE
  this.title.setTitle(pageTitle);

  // BASIC META
  this.meta.updateTag({
    name: 'description',
    content: description
  });

  this.meta.updateTag({
    name: 'robots',
    content:
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  });

  // OPEN GRAPH
  this.meta.updateTag({
    property: 'og:title',
    content: pageTitle
  });

  this.meta.updateTag({
    property: 'og:description',
    content: description
  });

  this.meta.updateTag({
    property: 'og:url',
    content: canonicalUrl
  });

  this.meta.updateTag({
    property: 'og:type',
    content: 'profile'
  });

  this.meta.updateTag({
    property: 'og:locale',
    content: 'en_IN'
  });

  this.meta.updateTag({
    property: 'og:site_name',
    content: 'Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:image',
    content: ogImage
  });

  this.meta.updateTag({
    property: 'og:image:alt',
    content: 'Mandavvi S. Mishra, Founder and Director of Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:image:type',
    content: 'image/jpeg'
  });

  // TWITTER / X
  this.meta.updateTag({
    name: 'twitter:card',
    content: 'summary_large_image'
  });

  this.meta.updateTag({
    name: 'twitter:title',
    content: pageTitle
  });

  this.meta.updateTag({
    name: 'twitter:description',
    content: description
  });

  this.meta.updateTag({
    name: 'twitter:image',
    content: ogImage
  });

  this.meta.updateTag({
    name: 'twitter:image:alt',
    content: 'Mandavvi S. Mishra, Founder and Director of Red Monk Wellness'
  });

  // CANONICAL URL
  let canonical = this.document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    this.document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);

  // SITEMAP
  let sitemap = this.document.querySelector(
    'link[rel="sitemap"]'
  ) as HTMLLinkElement | null;

  if (!sitemap) {
    sitemap = this.document.createElement('link');
    sitemap.setAttribute('rel', 'sitemap');
    sitemap.setAttribute('type', 'application/xml');
    sitemap.setAttribute('title', 'Sitemap');
    this.document.head.appendChild(sitemap);
  }

  sitemap.setAttribute(
    'href',
    'https://www.redmonkwellness.com/sitemap.xml'
  );

  // ORGANIZATION SCHEMA
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.redmonkwellness.com/#organization',
    name: 'Red Monk Wellness',
    url: 'https://www.redmonkwellness.com/',
    logo: 'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png',
    sameAs: [
      'https://www.linkedin.com/company/red-monk-wellness',
      'https://www.facebook.com/redmonkwellness'
    ]
  };

  // PERSON PROFILE SCHEMA
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${canonicalUrl}#person`,
    name: 'Mandavvi S. Mishra',
    jobTitle: 'Founder & Director',
    worksFor: {
      '@id': 'https://www.redmonkwellness.com/#organization'
    },
    url: canonicalUrl
  };

  // PROFILE PAGE SCHEMA
  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    headline: 'Mandavvi S. Mishra',
    description,
    mainEntity: {
      '@id': `${canonicalUrl}#person`
    },
    isPartOf: {
      '@id': 'https://www.redmonkwellness.com/#website'
    },
    inLanguage: 'en-IN',
    breadcrumb: {
      '@id': `${canonicalUrl}#breadcrumb`
    }
  };

  // BREADCRUMB SCHEMA
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.redmonkwellness.com/home'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Mandavvi S. Mishra',
        item: canonicalUrl
      }
    ]
  };

  // ADD / UPDATE JSON-LD SCHEMAS
  const schemas = [
    {
      id: 'organization-schema',
      data: organizationSchema
    },
    {
      id: 'person-schema',
      data: personSchema
    },
    {
      id: 'webpage-schema',
      data: webpageSchema
    },
    {
      id: 'breadcrumb-schema',
      data: breadcrumbSchema
    }
  ];

  schemas.forEach(({ id, data }) => {
    let script = this.document.getElementById(
      id
    ) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);
  });
}









}